import axios, { AxiosError } from 'axios';
import {
  ArbitrageResponse,
  SportsResponse,
  ApiError,
  EventsResponse,
  PlayerPropsResponse,
  EVResponse,
  Feedback,
  FeedbackStatus,
  FeedbackType,
} from './types';


type QueryParams = Record<string, string | number | boolean | undefined>;

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Inject Supabase auth token into every request
// Local variable to store the auth token
let authToken: string | null = null;

/**
 * Set the authentication token to be used for API requests
 * This should be called by the AuthContext whenever the session changes
 */
export const setAuthToken = (token: string | null) => {
  authToken = token;
};

// Inject Supabase auth token into every request
apiClient.interceptors.request.use(
  config => {
    if (authToken) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${authToken}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  response => response,
  (error: AxiosError<ApiError>) => {
    if (error.response) {
      // Server responded with error
      const responseData = error.response.data as ApiError | string | undefined;
      const message =
        typeof responseData === 'string'
          ? responseData
          : responseData?.message ||
          responseData?.error ||
          `Request failed with status ${error.response.status}`;
      console.error('API Error:', message);
      throw new Error(message);
    } else if (error.request) {
      // Request made but no response
      console.error('Network Error:', error.message);
      throw new Error('Network error. Please check your connection.');
    } else {
      // Error setting up request
      console.error('Error:', error.message);
      throw error;
    }
  }
);

export const api = {
  /**
   * Get all available sports
   */
  async getSports(activeOnly: boolean = false): Promise<SportsResponse> {
    const response = await apiClient.get<SportsResponse>('/api/sports', {
      params: { active: activeOnly },
    });
    return response.data;
  },

  /**
   * Get arbitrage opportunities
   */
  async getArbitrageOpportunities(params: {
    sports?: string[];
    regions?: string[];
    markets?: string[];
    oddsFormat?: 'decimal' | 'american';
    minProfit?: number;
    bookmakers?: string[];
    includeLiveGames?: boolean;
    playerPropMarkets?: string[];
  }): Promise<ArbitrageResponse> {
    const hasPlayerProps = (params.playerPropMarkets?.length || 0) > 0;
    const marketsValue = params.markets && params.markets.length > 0
      ? params.markets.join(',')
      : hasPlayerProps ? 'player_props' : 'h2h';
    const queryParams: QueryParams = {
      sports: params.sports?.join(',') || 'upcoming',
      markets: marketsValue,
      oddsFormat: params.oddsFormat || 'decimal',
      minProfit: params.minProfit ?? 0.5,
    };

    // If bookmakers are specified, use them instead of regions
    if (params.bookmakers && params.bookmakers.length > 0) {
      queryParams.bookmakers = params.bookmakers.join(',');
    } else {
      queryParams.regions = params.regions?.join(',') || 'us';
    }

    if (params.includeLiveGames !== undefined) {
      queryParams.includeLiveGames = params.includeLiveGames;
    }

    // Add player prop markets if specified
    if (params.playerPropMarkets && params.playerPropMarkets.length > 0) {
      queryParams.playerPropMarkets = params.playerPropMarkets.join(',');
    }

    const response = await apiClient.get<ArbitrageResponse>('/api/arbitrage', {
      params: queryParams,
    });
    return response.data;
  },

  /**
   * Check backend health
   */
  async checkHealth(): Promise<{ status: string; timestamp: string }> {
    const response = await apiClient.get('/health');
    return response.data;
  },

  /**
   * Get API usage statistics
   */
  async getUsageStats(): Promise<{
    requestsRemaining: number | null;
    requestsUsed: number | null;
    lastRequestCost: number | null;
    lastUpdated: string | null;
    summary: string;
  }> {
    const response = await apiClient.get('/api/usage');
    return response.data;
  },

  /**
   * Get NBA events available for player props
   */
  async getNBAEvents(): Promise<EventsResponse> {
    const response = await apiClient.get<EventsResponse>('/api/player-props/events/basketball_nba');
    return response.data;
  },

  /**
   * Get player prop arbitrage opportunities for an NBA event
   */
  async getPlayerPropOpportunities(params: {
    eventId: string;
    markets?: string[];
    regions?: string[];
    oddsFormat?: 'decimal' | 'american';
    minProfit?: number;
    bookmakers?: string[];
  }): Promise<PlayerPropsResponse> {
    const queryParams: QueryParams = {
      markets: params.markets?.join(',') || 'player_points,player_rebounds,player_assists,player_points_rebounds_assists',
      oddsFormat: params.oddsFormat || 'decimal',
      minProfit: params.minProfit ?? 0.5,
    };

    // If bookmakers are specified, use them instead of regions
    if (params.bookmakers && params.bookmakers.length > 0) {
      queryParams.bookmakers = params.bookmakers.join(',');
    } else {
      queryParams.regions = params.regions?.join(',') || 'us';
    }

    const response = await apiClient.get<PlayerPropsResponse>(
      `/api/player-props/basketball_nba/${params.eventId}`,
      { params: queryParams }
    );
    return response.data;
  },

  /**
   * Get +EV betting opportunities
   */
  async getEVOpportunities(params: {
    sports?: string[];
    markets?: string[];
    regions?: string[];
    oddsFormat?: 'decimal' | 'american';
    minEV?: number;
    bookmakers?: string[];
    includeLiveGames?: boolean;
    playerPropMarkets?: string[];
  }): Promise<EVResponse> {
    // Combine game markets and player prop markets
    const allMarkets = [
      ...(params.markets || []),
      ...(params.playerPropMarkets || [])
    ];

    const queryParams: QueryParams = {
      sports: params.sports?.join(',') || 'upcoming',
      markets: allMarkets.length > 0 ? allMarkets.join(',') : 'h2h',
      regions: params.regions?.join(',') || 'us',
      oddsFormat: params.oddsFormat || 'american',
      minEV: params.minEV ?? 1.0,
    };

    // If bookmakers are specified, add to params
    if (params.bookmakers && params.bookmakers.length > 0) {
      queryParams.bookmakers = params.bookmakers.join(',');
    }

    if (params.includeLiveGames !== undefined) {
      queryParams.includeLiveGames = params.includeLiveGames;
    }

    const response = await apiClient.get<EVResponse>('/api/ev', {
      params: queryParams,
    });
    return response.data;
  },

  /**
   * Submit feedback (bug report or improvement suggestion)
   */
  async submitFeedback(payload: {
    type: FeedbackType;
    title: string;
    description: string;
  }): Promise<{ message: string; feedback: Feedback }> {
    const response = await apiClient.post<{ message: string; feedback: Feedback }>('/api/feedback', payload);
    return response.data;
  },

  /**
   * Get all feedback (admin only)
   */
  async getAllFeedback(params?: {
    status?: FeedbackStatus;
    type?: FeedbackType;
  }): Promise<{ feedback: Feedback[] }> {
    const response = await apiClient.get<{ feedback: Feedback[] }>('/api/feedback', { params });
    return response.data;
  },

  /**
   * Update feedback status (admin only)
   */
  async updateFeedbackStatus(id: string, status: FeedbackStatus): Promise<{ message: string; feedback: Feedback }> {
    const response = await apiClient.patch<{ message: string; feedback: Feedback }>(`/api/feedback/${id}`, { status });
    return response.data;
  },

  /**
   * SUBSCRIPTION METHODS
   */

  /**
   * Get available subscription tiers
   */
  async getSubscriptionTiers(): Promise<{
    tiers: {
      free: {
        tier: string;
        name: string;
        prices: {
          daily: { price: number; period: string };
          monthly: { price: number; period: string };
          yearly: { price: number; period: string };
        };
        features: string[];
      };
      gold: {
        tier: string;
        name: string;
        prices: {
          daily: { price: number; period: string };
          monthly: { price: number; period: string };
          yearly: { price: number; period: string };
        };
        features: string[];
      };
      platinum: {
        tier: string;
        name: string;
        prices: {
          daily: { price: number; period: string };
          monthly: { price: number; period: string };
          yearly: { price: number; period: string };
        };
        features: string[];
      };
    };
  }> {
    const response = await apiClient.get('/api/subscriptions/tiers');
    return response.data;
  },

  /**
   * Create a Stripe checkout session for subscription
   */
  async createSubscriptionCheckout(payload: {
    tier: 'gold' | 'platinum';
    billingPeriod?: 'daily' | 'monthly' | 'yearly';
    successUrl?: string;
    cancelUrl?: string;
  }): Promise<{
    sessionId: string;
    checkoutUrl: string;
    tier: string;
    price: number;
    billingPeriod: string;
    period: string;
  }> {
    const response = await apiClient.post('/api/subscriptions/create-checkout', payload);
    return response.data;
  },

  /**
   * Create a Stripe customer portal session for subscription management
   */
  async createPortalSession(returnUrl?: string): Promise<{ url: string }> {
    const response = await apiClient.post('/api/subscriptions/create-portal-session', {
      returnUrl,
    });
    return response.data;
  },

  /**
   * Get current subscription status and usage
   */
  async getSubscriptionStatus(): Promise<{
    subscription: {
      tier: string;
      status: string;
      currentPeriodEnd: string | null;
      trialEndsAt: string | null;
      isActive: boolean;
    };
    usage: {
      today: {
        arbitrage_searches: number;
        ev_searches: number;
        player_prop_searches: number;
      };
      limits: {
        arbitrageSearches: number;
        evSearches: number;
        playerPropSearches: number;
        liveData: boolean;
      };
    };
  }> {
    const response = await apiClient.get('/api/subscriptions/status');
    return response.data;
  },

  /**
   * Cancel subscription (at period end)
   */
  async cancelSubscription(): Promise<{
    success: boolean;
    cancelsAt: number;
    message: string;
  }> {
    const response = await apiClient.post('/api/subscriptions/cancel');
    return response.data;
  },

  /**
   * Reactivate a canceled subscription
   */
  async reactivateSubscription(): Promise<{
    success: boolean;
    status: string;
    message: string;
  }> {
    const response = await apiClient.post('/api/subscriptions/reactivate');
    return response.data;
  },

  /**
   * Manually sync subscription from Stripe
   * Useful when webhooks fail or for local development
   */
  async syncSubscription(): Promise<{
    success: boolean;
    message: string;
    subscription: {
      tier: string;
      status: string;
      subscriptionId: string;
      currentPeriodEnd: string;
      trialEnd: string | null;
    };
    updated?: {
      subscription_tier: string;
      subscription_status: string;
      stripe_subscription_id: string;
    };
  }> {
    const response = await apiClient.post('/api/sync-subscription');
    return response.data;
  },
};

export default api;
